import React from "react";
import {
    checkIsFileForbiddenType,
    getFileSizeString,
} from "repoV2/features/Common/utils/file";
import { logError } from "repoV2/utils/common/error/error";
import { DEPRECATED_S3_BUCKET } from "repoV2/features/Common/modules/S3/S3.constants";
import { uploadToS3 } from "../../../S3/S3.utils";
import {
    DEFAULT_FILE_UPLOAD_SIZE_IN_BYTES,
    FILE_MIME_IMAGE_FORMAT,
    FILE_MIME_VIDEO_FORMAT,
} from "../../../Files/Files.constants";
import { default_file_uploaded_state } from "./ExlyFileUpload.constants";

/**
 * Converts a base64 data URL into a File object.
 *
 * @param {string} dataurl - The base64 encoded data URL.
 * @param {string} filename - The name to assign to the resulting File object.
 * @returns {File} - The converted File object.
 */
export const dataURLtoFile = (
    dataurl: string,
    filename: string
): File | null => {
    try {
        // Split the data URL into an array [header, base64 string]
        const arr = dataurl.split(",");

        // Extract the MIME type from the header part of the data URL
        const mime = arr[0].match(/:(.*?);/)?.[1];

        // Decode the base64 string to binary data
        const bstr = atob(arr[arr.length - 1]);

        // Create a Uint8Array to hold the binary data
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        // Fill the Uint8Array with the binary data (character codes)
        // eslint-disable-next-line no-plusplus
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        // Return a new File object created from the binary data
        return new File([u8arr], filename, { type: mime });
    } catch (err) {
        return null;
    }
};

/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsForRegex": ["^videoElement"] }] */

/**
 * Waits for a video element to finish loading metadata (duration, dimensions, etc.).
 *
 * @param {HTMLVideoElement} videoElement - The video element to monitor.
 * @returns {Promise<void>} - Resolves when the metadata is loaded, rejects on error.
 */
function loadVideoData(videoElement: HTMLVideoElement): Promise<void> {
    return new Promise((resolve, reject) => {
        // Resolve the promise when the video data is fully loaded
        videoElement.onloadeddata = () => resolve();

        // Reject the promise if there's an error loading the video
        videoElement.onerror = () =>
            reject(new Error("Error loading video data."));
    });
}

/**
 * Seeks the video element to a specific time and waits until the seek is complete.
 *
 * @param {HTMLVideoElement} videoElement - The video element to seek.
 * @param {number} captureTime - The time (in seconds) to seek to.
 * @returns {Promise<void>} - Resolves when the seek operation completes, rejects on error.
 */
function seekVideoTime(
    videoElement: HTMLVideoElement,
    captureTime: number
): Promise<void> {
    return new Promise((resolve, reject) => {
        // Resolve the promise when the video has successfully sought to the capture time
        videoElement.onseeked = () => resolve();

        // Reject the promise if an error occurs during seeking
        videoElement.onerror = () => reject(new Error("Error seeking video."));

        // Set the video to the specified capture time to trigger the seek
        videoElement.currentTime = captureTime;
    });
}

/**
 * Extracts a thumbnail image from a video file at a specified capture time.
 *
 * @param {File} videoFile - The video file from which to extract the thumbnail.
 * @param {string} thumbnailFilename - The filename for the generated thumbnail image.
 * @returns {Promise<File>} - A promise that resolves to a File object representing the thumbnail image.
 */
export const extractVideoThumbnail = async (
    videoFile: File,
    thumbnailFilename: string
): Promise<File | null> => {
    // Create a video element to load the video file
    const videoElement = document.createElement("video");

    // Create a canvas element to draw the video frame
    const canvasElement = document.createElement("canvas");

    // Create a URL for the video file to be loaded into the video element
    const videoURL = URL.createObjectURL(videoFile);

    // Assign the video URL to the video element and start playback (muted)
    videoElement.src = videoURL;
    videoElement.muted = true;
    videoElement.play();

    // Wait for the video metadata to load (dimensions, etc.)
    await loadVideoData(videoElement);

    // Seek the video to the 1-second mark to capture the thumbnail
    await seekVideoTime(videoElement, 1);

    // Set the canvas size to match the video's dimensions
    canvasElement.width = videoElement.videoWidth;
    canvasElement.height = videoElement.videoHeight;

    // Get the canvas drawing context and draw the current video frame onto it
    const ctx = canvasElement.getContext("2d");
    ctx?.drawImage(
        videoElement,
        0,
        0,
        videoElement.videoWidth,
        videoElement.videoHeight
    );

    // Convert the canvas content to a base64 data URL (PNG format)
    const imageUrl = canvasElement.toDataURL("image/png");

    // Clean up resources by revoking the video URL and removing the video element
    URL.revokeObjectURL(videoURL);
    videoElement.remove();

    // Convert the base64 image data to a File object and return it
    return dataURLtoFile(imageUrl, thumbnailFilename);
};

export const useExlyFileUpload = (props: {
    onChange: ({
        fileKey,
        fileData,
    }: {
        fileKey: string;
        fileData: {
            type: string;
            value: string;
            thumbnail?: string;
        };
    }) => void;
    onClear: () => void;
    generateUploadKey?: (file: File) => string;
    name: string;
    clearErrors: (name: string) => void;
    handleFileUploadStart: () => void;
    handleFileUploadEnd: () => void;
    alreadyUploadedFile: {
        type: string;
        value: string;
        thumbnail?: string;
    };
    accept: Array<string>;
}) => {
    const {
        onChange,
        onClear,
        generateUploadKey,
        name = "",
        clearErrors,
        handleFileUploadStart,
        handleFileUploadEnd,
        alreadyUploadedFile,
        accept,
    } = props;

    const [fileUploaded, setFileUploaded] = React.useState<{
        type?: string;
        value?: string;
        thumbnail?: string;
    }>(alreadyUploadedFile || {});
    const [isFileUploading, setIsFileUploading] = React.useState(false);
    const [customErrorMessage, setCustomErrorMessage] = React.useState("");

    const handleFileTypeValidations = (file: File) => {
        const validTypes = accept.map(type => {
            // Let say file type is 'image/*', so the returning type will be 'image'
            if (type.includes("*")) {
                return type.split("/")[0];
            }
            // Else, it was like 'application/pdf', so the returning type will be the whole value
            return type;
        });

        // From the uploaded file type, say 'image/svg',
        // 'image' is the file type
        // 'image/svg is the complete file extention
        const fileType = file.type.split("/")[0];
        const fileExtension = file.type;

        // So, 'validTypes.includes(fileType)' this is done to check the files which were uploaded where all formats are accepted (* case) e.g. image/*
        // 'accept.includes(fileExtension)', this checks for the specific files types that needs to be verified, e.g. application/pdf
        const isValidType =
            validTypes.includes(fileType) || accept.includes(fileExtension);

        if (!isValidType) {
            setCustomErrorMessage("Invalid file type!");
            return false;
        }
        return true;
    };

    const handleFileInputChange = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        // The stopPropagation() method prevents propagation of the same event from being called.
        // Propagation means bubbling up to parent elements or capturing down to child elements.
        // In this case, we have to prevent the onclick of the parent button element it is enclosed in.
        e.stopPropagation();
        if (e.target.value?.length && e.target.files) {
            const isFileTypeValid = handleFileTypeValidations(
                e.target.files[0]
            );

            if (!isFileTypeValid) {
                return;
            }

            const { uploadFile, url, thumbnail } = await uploadFileToServer(
                e.target.files && e.target.files[0]
            );
            const file = e.target.files && e.target.files[0];

            if (uploadFile && file) {
                clearErrors(name);
                onChange({
                    fileKey: uploadFile,
                    fileData: {
                        type: file.type,
                        value: url,
                        thumbnail,
                    },
                });
            }
        }
    };

    const handleClearFileInputChange = () => {
        setFileUploaded({});
        onClear(); // clear field?
    };

    const uploadFileToServer = async (file: File | null) => {
        if (!file) {
            return default_file_uploaded_state;
        }
        setCustomErrorMessage("");
        try {
            setIsFileUploading(true);
            handleFileUploadStart();

            // check if the file type is forbidden, if yes show error message accordingly
            const isFileForbiddenType = checkIsFileForbiddenType(file);
            if (isFileForbiddenType) {
                setCustomErrorMessage("Invalid file type.");
                return default_file_uploaded_state;
            }

            // check if the file size is valid, show error if it more than max file size
            if (file.size > DEFAULT_FILE_UPLOAD_SIZE_IN_BYTES) {
                setCustomErrorMessage(
                    `File must be smaller than ${
                        DEFAULT_FILE_UPLOAD_SIZE_IN_BYTES / (1024 * 1024)
                    }MB (File submitted was ${
                        getFileSizeString(file.size, {
                            precision: 4,
                        }).outputString
                    })`
                );
                return default_file_uploaded_state;
            }

            const thumbnail = Object.values(FILE_MIME_VIDEO_FORMAT)
                .flat()
                .includes(file.type)
                ? await extractVideoThumbnail(
                      file,
                      `${new Date().getTime()}_thumbnail.jpg`
                  )
                : null;

            /**
             * Todo - Need to switch to multipart upload once
             * the file size increases
             */
            // Upload the file to S3
            const fileResponse = await uploadToS3({
                file,
                key: generateUploadKey?.(file),
                bucket: DEPRECATED_S3_BUCKET,
                isFilePublic: true,
            });
            if (!fileResponse) throw new Error("no fileResponse");
            const url = fileResponse.Location;

            let thumbnailFileResponse;
            if (thumbnail) {
                try {
                    thumbnailFileResponse = await uploadToS3({
                        file: thumbnail,
                        key: generateUploadKey?.(file),
                        bucket: DEPRECATED_S3_BUCKET,
                        isFilePublic: true,
                    });
                } catch (e) {
                    logError({
                        error: e,
                        occuredAt:
                            "repoV2/features/Common/modules/Form/modules/ExlyFileUpload/ExlyFileUpload.utils.ts",
                        when: "calling uploadToS3",
                    });
                }
            }

            let value;
            // checking for the file format, if it is image/video, we get a signed url, in case pf application(eg pdf),
            // we just need the name of the file.
            if (
                Object.values(FILE_MIME_IMAGE_FORMAT)
                    .flat()
                    .includes(file.type) ||
                Object.values(FILE_MIME_VIDEO_FORMAT).flat().includes(file.type)
            ) {
                value = `${url}`;
            } else {
                value = file.name;
            }

            setFileUploaded({
                type: file.type,
                value,
                thumbnail: thumbnailFileResponse?.Key,
            });

            return {
                uploadFile: fileResponse.Key || "",
                url: value,
                thumbnail: thumbnailFileResponse?.Key,
            };
        } catch (err) {
            logError({
                error: err,
                occuredAt:
                    "repoV2/features/Common/modules/Form/modules/ExlyFileUpload/ExlyFileUpload.tsx",
                when: "inside uploadFileToServer when uploading the file",
            });
            setCustomErrorMessage("Error Uploading. Please try again.");
            return default_file_uploaded_state;
        } finally {
            setIsFileUploading(false);
            handleFileUploadEnd();
        }
    };

    return {
        fileUploaded,
        isFileUploading,
        customErrorMessage,
        handleFileInputChange,
        handleClearFileInputChange,
    };
};
